import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import { LanguageSelector } from "./LanguageSelector";

export const Navbar = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const clickCountRef = useRef(0);
  const clickTimerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    return () => {
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    };
  }, []);

  const handleLogoClick = (e) => {
    e.preventDefault();
    clickCountRef.current += 1;

    if (clickCountRef.current === 3) {
      // Triple click → admin login
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
      clickCountRef.current = 0;
      navigate("/paga-admin");
      return;
    }

    // Reset counter after 600ms window
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    clickTimerRef.current = setTimeout(() => {
      if (clickCountRef.current === 1) {
        // Single click → scroll to top / home
        if (window.location.pathname !== "/") {
          navigate("/");
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }
      clickCountRef.current = 0;
    }, 600);
  };

  const navItems = [
    { label: t("nav.about"), href: "#about" },
    { label: t("nav.collection"), href: "#gallery" },
    { label: t("nav.location"), href: "#location" },
    { label: t("nav.contact"), href: "#contact" },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6">
        {/* Mobile-only: two-row layout (logo/lang on top, nav grid below) */}
        <div className="sm:hidden">
          <div className="flex items-center justify-between gap-2">
            <a
              href="/"
              onClick={handleLogoClick}
              className="flex items-center flex-shrink-0 cursor-pointer select-none"
              data-testid="logo"
              title="Locker Room"
            >
              <img
                src="/locker-room-logo.png"
                alt={t("alts.logo")}
                className="h-9 w-auto pointer-events-none"
                draggable={false}
              />
            </a>
            <div className="flex items-center justify-end flex-shrink-0">
              <LanguageSelector />
            </div>
          </div>
          <div
            className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5"
            data-testid="mobile-nav-grid"
          >
            {navItems.map((item) => (
              <button
                key={item.href}
                onClick={() => scrollToSection(item.href)}
                className="nav-link font-body text-xs tracking-[0.15em] uppercase text-center py-1"
                data-testid={`nav-${item.href.replace("#", "")}`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* sm+ : original single-row layout */}
        <div className="hidden sm:flex items-center justify-between gap-3">
          <a
            href="/"
            onClick={handleLogoClick}
            className="flex items-center flex-shrink-0 cursor-pointer select-none"
            data-testid="logo-desktop"
            title="Locker Room"
          >
            <img
              src="/locker-room-logo.png"
              alt={t("alts.logo")}
              className="h-10 md:h-12 w-auto pointer-events-none"
              draggable={false}
            />
          </a>

          <div className="flex items-center justify-center gap-6 md:gap-10 xl:gap-14 mx-auto flex-1 min-w-0">
            {navItems.map((item) => (
              <button
                key={item.href}
                onClick={() => scrollToSection(item.href)}
                className="nav-link font-body text-xs md:text-sm tracking-[0.15em] md:tracking-[0.2em] uppercase whitespace-nowrap"
                data-testid={`nav-desktop-${item.href.replace("#", "")}`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-end flex-shrink-0">
            <LanguageSelector />
          </div>
        </div>
      </div>
    </motion.nav>
  );
};
